import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  Button,
  Divider,
  Card,
  CardContent,
  ListItemIcon,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  reportsApi,  // ✅ Changed to use grouped API
  patientsApi  // Already using grouped API
} from "../services/api";  // Updated import
import { useNavigate } from "react-router-dom";

// ✅ Import meaningful icons
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import InventoryIcon from '@mui/icons-material/Inventory';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import StorefrontIcon from '@mui/icons-material/Storefront';
import WarningIcon from '@mui/icons-material/Warning';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReportIcon from '@mui/icons-material/Report';     // For expired
import WarningAmberIcon from '@mui/icons-material/WarningAmber'; // For near expiry
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CalculateIcon from '@mui/icons-material/Calculate';
import PeopleIcon from '@mui/icons-material/People'; // Add this import

import ApexSalesChart from "../components/charts/ApexSalesChart";
import ApexTopMedicinesChart from "../components/charts/ApexTopMedicinesChart";
import ApexInventoryChart from "../components/charts/ApexInventoryChart";
import ApexRevenueChart from "../components/charts/ApexRevenueChart";
import DashboardSkeleton from "../components/skeletons/DashboardSkeleton";
import DirectiveCard from "../components/DirectiveCard";
// ✅ Add translation hook
import { useTranslation } from 'react-i18next';

export default function DashboardPage() {
  const [salesData, setSalesData] = useState({
    totalSales: 0,
    orderCount: 0,
    avgOrderValue: 0,
    dailySales: [],
  });
  const [lowStockAlerts, setLowStockAlerts] = useState([]);
  const [expiringSoonAlerts, setExpiringSoonAlerts] = useState([]);
  const [topMedicines, setTopMedicines] = useState([]);
  const [revenueByCategory, setRevenueByCategory] = useState([]);
  const [patientCount, setPatientCount] = useState(0);
  const [prescriptions, setPrescriptions] = useState({ total: 0, fulfilled: 0, pending: 0, rate: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();

  // ✅ Initialize translation
  const { t } = useTranslation();

  const fetchDashboardData = React.useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const results = await Promise.allSettled([
        reportsApi.getDailySales().catch(err => ({ err })),
        reportsApi.getLowStockAlerts(5).catch(err => ({ err })),
        reportsApi.getExpiringSoonAlerts(30).catch(err => ({ err })),
        reportsApi.getTopMedicines(5).catch(err => ({ err })),
        reportsApi.getRevenueByCategory().catch(err => ({ err })),
        reportsApi.getPrescriptionFulfillmentRate().catch(err => ({ err }))
      ]);

      console.log("📊 API Results:", results);

      let hasAnySuccess = false;

      // ... (existing handlers)
      if (results[0].status === 'fulfilled' && results[0].value?.data) {
        const data = results[0].value.data.report || results[0].value.data;
        const total = parseFloat(data.total || data.totalSales || data.amount || data.revenue || 0);
        const count = parseInt(data.count || data.orderCount || data.orders || data.transactions || 0);
        const dailySalesArray = data.dailySales || data.sales || data.data || data.chartData || [];
        setSalesData({ totalSales: total, orderCount: count, avgOrderValue: count > 0 ? total / count : 0, dailySales: dailySalesArray });
        hasAnySuccess = true;
      }
      if (results[1].status === 'fulfilled' && results[1].value?.data) {
        const responseData = results[1].value.data;
        const rawData = Array.isArray(responseData.batches) ? responseData.batches : (Array.isArray(responseData) ? responseData : []);
        setLowStockAlerts(rawData.map(item => ({
          medicineName: item.Medicine?.name || item.medicineName || item.name || t('common.unknownMedicine'),
          batchNumber: item.batchNumber || item.batch || t('common.na'),
          quantity: item.quantity || item.stock || item.remaining || 0,
          expiryDate: item.expiryDate || item.expiry || t('common.unknown')
        })));
        hasAnySuccess = true;
      }
      if (results[2].status === 'fulfilled' && results[2].value?.data) {
        const responseData = results[2].value.data;
        const rawData = Array.isArray(responseData.batches) ? responseData.batches : (Array.isArray(responseData) ? responseData : []);
        setExpiringSoonAlerts(rawData.map(item => ({
          medicineName: item.Medicine?.name || item.medicineName || item.name || t('common.unknownMedicine'),
          batchNumber: item.batchNumber || item.batch || t('common.na'),
          quantity: item.quantity || item.stock || 0,
          expiryDate: item.expiryDate || item.expiry || t('common.unknown')
        })));
        hasAnySuccess = true;
      }
      if (results[3].status === 'fulfilled' && results[3].value?.data) {
        const responseData = results[3].value.data;
        const rawData = Array.isArray(responseData.items) ? responseData.items : (Array.isArray(responseData) ? responseData : []);
        setTopMedicines(rawData.filter(Boolean).map(item => ({
          name: item.name || item.medicineName || item.medicine?.name || item.productName || t('common.unknownMedicine'),
          quantity: parseInt(item.quantity || item.totalQuantity || item.salesCount || 0)
        })));
        hasAnySuccess = true;
      }
      if (results[4].status === 'fulfilled' && results[4].value?.data) {
        setRevenueByCategory(results[4].value.data);
        hasAnySuccess = true;
      }

      // Handle Prescription Rate
      if (results[5].status === 'fulfilled' && results[5].value?.data) {
        const data = results[5].value.data;
        setPrescriptions({
          total: data.total || 0,
          fulfilled: data.fulfilled || 0,
          pending: (data.total || 0) - (data.fulfilled || 0),
          rate: parseFloat(data.rate || data.fulfillmentRate || 0)
        });
        hasAnySuccess = true;
      }

      // Fetch patient count
      try {
        const patientsRes = await patientsApi.getAll();
        setPatientCount(patientsRes.data?.length || 0);
      } catch (patientErr) {
        console.error("Failed to fetch patient count:", patientErr);
      }

      // Fallback demo data if needed
      if (!hasAnySuccess) {
        setSalesData({
          totalSales: 2875.50,
          orderCount: 23,
          avgOrderValue: 125.02,
          dailySales: Array.from({ length: 7 }, (_, i) => ({ date: `2024-01-01T00:00:00.000Z`, amount: Math.random() * 2000 }))
        });
        setTopMedicines([
          { name: "Paracetamol", quantity: 156 },
          { name: "Amoxicillin", quantity: 89 },
          { name: "Vitamin C", quantity: 67 },
          { name: "Ibuprofen", quantity: 54 },
          { name: "Aspirin", quantity: 42 }
        ]);
        setRevenueByCategory([
          { category: "Antibiotics", revenue: 5000 },
          { category: "Painkillers", revenue: 3000 },
          { category: "Chronic", revenue: 2000 }
        ]);
        setPrescriptions({ total: 45, fulfilled: 38, pending: 7, rate: 84.4 });
      }

    } catch (err) {
      console.error("Unexpected dashboard error:", err);
      setError(t('dashboard.unexpectedError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading) return <Container maxWidth="xl" sx={{ mt: 4 }}><DashboardSkeleton /></Container>;

  const inventoryChartData = [
    { name: t('dashboard.inStock'), value: Math.max(1, topMedicines.reduce((sum, m) => sum + (m.quantity || 0), 0)) },
    { name: t('dashboard.lowStock'), value: lowStockAlerts.length },
    { name: t('dashboard.expiringSoon'), value: expiringSoonAlerts.length },
  ].filter(item => item.value > 0);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6, px: 3 }}>
      {/* Premium Header */}
      <Box
        sx={{
          textAlign: "center",
          mb: 6,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          borderRadius: 4,
          py: 5,
          color: 'white',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <StorefrontIcon sx={{ fontSize: 64, mb: 2, opacity: 0.9 }} />
          <Typography variant="h3" fontWeight="900" gutterBottom sx={{ letterSpacing: '-1px' }}>
            {t('dashboard.title')}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
            {t('dashboard.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
      </Box>

      {/* KPI Section */}
      <Grid container spacing={3} sx={{ mb: 6 }}>
        {[
          { icon: <AttachMoneyIcon />, label: t('dashboard.totalSalesToday'), value: `$${salesData.totalSales.toFixed(2)}`, color: theme.palette.primary.main },
          { icon: <ReceiptIcon />, label: t('dashboard.ordersProcessed'), value: salesData.orderCount, color: theme.palette.secondary.main },
          { icon: <PeopleIcon />, label: t('dashboard.totalPatients'), value: patientCount, color: theme.palette.info.main },
          { icon: <CalculateIcon />, label: t('dashboard.avgOrderValue'), value: `$${salesData.avgOrderValue.toFixed(2)}`, color: theme.palette.success.main },
        ].map((kpi, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <Paper
              sx={{
                p: 3,
                textAlign: "center",
                background: `linear-gradient(135deg, ${alpha(kpi.color, 0.05)} 0%, ${alpha(kpi.color, 0.15)} 100%)`,
                border: `1px solid ${alpha(kpi.color, 0.2)}`,
                borderRadius: 4,
                transition: 'all 0.3s ease',
                '&:hover': { transform: 'translateY(-5px)', boxShadow: `0 10px 30px ${alpha(kpi.color, 0.2)}` }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1, color: kpi.color }}>
                {React.cloneElement(kpi.icon, { sx: { fontSize: 24, mr: 1 } })}
                <Typography variant="subtitle2" color="textSecondary" fontWeight="600" sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {kpi.label}
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="800" color={kpi.color}>
                {kpi.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Primary Section: Sales Trend & Operational Cards (Perfectly Balanced 1:1:1) */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        {/* Sales Trend Chart (Width: 4) */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.06)', overflow: 'hidden', height: '100%' }}>
            <Box sx={{ p: 4, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <TrendingUpIcon color="primary" />
                <Typography variant="h6" fontWeight="800">{t('dashboard.salesTrends')}</Typography>
              </Box>
            </Box>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ height: 420, width: '100%' }}>
                <ApexSalesChart data={salesData.dailySales} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Operational Performance Card (Width: 4) */}
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.06)', height: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="subtitle2" color="textSecondary" fontWeight="700" sx={{ mb: 3, textTransform: 'uppercase' }}>
                {t('dashboard.operationalPerformance')}
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" fontWeight="600">{t('dashboard.prescriptionFulfillment')}</Typography>
                  <Typography variant="body2" color="primary" fontWeight="700">{prescriptions.rate}%</Typography>
                </Box>
                <Box sx={{ width: '100%', height: 10, bgcolor: alpha(theme.palette.primary.main, 0.1), borderRadius: 5 }}>
                  <Box sx={{ width: `${prescriptions.rate}%`, height: '100%', bgcolor: theme.palette.primary.main, borderRadius: 5 }} />
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ p: 3, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 3, border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`, textAlign: 'center' }}>
                    <Typography variant="caption" color="textSecondary" display="block">{t('common.complete')}</Typography>
                    <Typography variant="h5" fontWeight="900" color="success.main">{prescriptions.fulfilled}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ p: 3, bgcolor: alpha(theme.palette.warning.main, 0.05), borderRadius: 3, border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`, textAlign: 'center' }}>
                    <Typography variant="caption" color="textSecondary" display="block">Pending</Typography>
                    <Typography variant="h5" fontWeight="900" color="warning.main">{prescriptions.pending}</Typography>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ mt: 4, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 3 }}>
                <Typography variant="body2" color="info.main" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MedicalInformationIcon fontSize="small" /> {t('dashboard.systemEfficiencyOptimal')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Business Context Card (Width: 4) */}
        <Grid item xs={12} md={6} lg={4}>
          <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.06)', height: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="subtitle2" color="textSecondary" fontWeight="700" sx={{ mb: 3, textTransform: 'uppercase' }}>
                {t('dashboard.businessContext')}
              </Typography>

              <List disablePadding>
                <ListItem disableGutters sx={{ mb: 4, p: 2, bgcolor: alpha(theme.palette.background.default, 0.5), borderRadius: 3 }}>
                  <ListItemIcon sx={{ minWidth: 50 }}>
                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                      <PeopleIcon color="info" />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="h6" fontWeight="800">{patientCount}</Typography>}
                    secondary={t('dashboard.totalRegisteredPatients')}
                  />
                </ListItem>

                <ListItem disableGutters sx={{ p: 2, bgcolor: alpha(theme.palette.background.default, 0.5), borderRadius: 3 }}>
                  <ListItemIcon sx={{ minWidth: 50 }}>
                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.secondary.main, 0.1) }}>
                      <ReceiptIcon color="secondary" />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="h6" fontWeight="800">{salesData.orderCount}</Typography>}
                    secondary={t('dashboard.activeSalesTransactions')}
                  />
                </ListItem>
              </List>

              <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${theme.palette.divider}`, textAlign: 'right' }}>
                <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic', bgcolor: alpha(theme.palette.divider, 0.1), px: 1, py: 0.5, borderRadius: 1 }}>
                  Live Data: {new Date().toLocaleTimeString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Secondary Charts - Balanced Grid (3 columns) */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        {/* Top Medicines */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.06)', height: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 4 }}>
                <LocalHospitalIcon color="secondary" />
                <Typography variant="h6" fontWeight="800">{t('dashboard.topMedicines')}</Typography>
              </Box>
              <Box sx={{ height: 350 }}>
                <ApexTopMedicinesChart data={topMedicines} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Inventory Status */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.06)', height: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 4 }}>
                <InventoryIcon color="warning" />
                <Typography variant="h6" fontWeight="800">{t('dashboard.inventoryStatus')}</Typography>
              </Box>
              <Box sx={{ height: 350 }}>
                <ApexInventoryChart data={inventoryChartData} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue By Category */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.06)', height: '100%' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 4 }}>
                <AttachMoneyIcon color="success" />
                <Typography variant="h6" fontWeight="800">{t('dashboard.revenueBreakdown')}</Typography>
              </Box>
              <Box sx={{ height: 350 }}>
                <ApexRevenueChart data={revenueByCategory} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts Row */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 4, borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: lowStockAlerts.length > 0 ? `1px solid ${alpha(theme.palette.error.main, 0.2)}` : 'none' }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <WarningIcon color="error" />
                <Typography variant="h6" fontWeight="800">{t('dashboard.lowStockAlerts')}</Typography>
              </Box>
              <Chip label={lowStockAlerts.length} color="error" size="small" />
            </Box>
            <Divider sx={{ mb: 2 }} />
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {lowStockAlerts.length === 0 ? (
                <Typography color="textSecondary" align="center" py={4}>No low stock alerts</Typography>
              ) : (
                lowStockAlerts.map((item, i) => (
                  <ListItem key={i} sx={{ px: 0 }}>
                    <ListItemText primary={item.medicineName} secondary={`Qty: ${item.quantity}`} />
                    <Chip label="Critical" color="error" variant="outlined" size="small" />
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 4, borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: expiringSoonAlerts.length > 0 ? `1px solid ${alpha(theme.palette.warning.main, 0.2)}` : 'none' }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <AccessTimeIcon color="warning" />
                <Typography variant="h6" fontWeight="800">{t('dashboard.expiringSoon')}</Typography>
              </Box>
              <Chip label={expiringSoonAlerts.length} color="warning" size="small" />
            </Box>
            <Divider sx={{ mb: 2 }} />
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {expiringSoonAlerts.length === 0 ? (
                <Typography color="textSecondary" align="center" py={4}>No expiring soon alerts</Typography>
              ) : (
                expiringSoonAlerts.map((item, i) => (
                  <ListItem key={i} sx={{ px: 0 }}>
                    <ListItemText primary={item.medicineName} secondary={`Exp: ${item.expiryDate}`} />
                    <Chip label="Near Expiry" color="warning" variant="outlined" size="small" />
                  </ListItem>
                ))
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Directive Section */}
      <Box sx={{ mt: 8 }}>
        <Typography variant="h5" fontWeight="900" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
          {t('dashboard.latestRegulatoryDirective')}
        </Typography>
        <DirectiveCard compact={false} />
      </Box>
    </Container>
  );
}
